import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import OrdersView from '@/components/OrderStats/OrdersView';
import OrdersSummary from '@/components/OrderStats/OrdersSummary';
import { safeExecute } from '@/utils/environment';
import { StatusCount, RecentOrder, OrderDetails } from '@/components/OrderStats/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAdminAuth } from '@/context/AdminAuthContext';

const OrderStatsViewer = () => {
  const { adminUser } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{
    totalCount: number;
    statusCounts: StatusCount[];
    recentOrders: RecentOrder[];
    orders: OrderDetails[];
    pagination: {
      page: number;
      pageSize: number;
      totalPages: number;
      totalCount: number;
    }
  }>({
    totalCount: 0,
    statusCounts: [],
    recentOrders: [],
    orders: [],
    pagination: {
      page: 1,
      pageSize: 10,
      totalPages: 1,
      totalCount: 0
    }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Function to fetch payment orders data
  const fetchPaymentOrders = async (page: number = 1) => {
    // Ensure admin user is logged in
    if (!adminUser) {
      console.error('No admin user logged in');
      toast.error('Please log in as an admin');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching payment orders data for admin:', adminUser.email);
      
      // Total count
      const { count: totalCount, error: countError } = await supabase
        .from('payment_orders')
        .select('*', { count: 'exact', head: true });
      
      if (countError) {
        console.error('Error fetching total count:', countError);
        throw countError;
      }
      
      console.log(`Total order count: ${totalCount}`);
      
      // Status counts
      const statusPromises = ['PAID', 'PENDING', 'FAILED'].map(async (status) => {
        const { count: statusCount, error } = await supabase
          .from('payment_orders')
          .select('*', { count: 'exact', head: true })
          .eq('status', status);
        
        if (error) {
          console.error(`Error counting ${status} orders:`, error);
          return { status, count: 0 };
        }
        
        return { status, count: statusCount || 0 };
      });
      
      const statusCounts = await Promise.all(statusPromises);
      console.log('Status counts:', statusCounts);
      
      // Recent orders
      const { data: recentOrdersData, error: recentError } = await supabase
        .from('payment_orders')
        .select('order_id, user_email, amount, status, created_at, product_name')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (recentError) {
        console.error('Error fetching recent orders:', recentError);
        throw recentError;
      }
      
      // Paginated orders
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data: ordersData, error: ordersError } = await supabase
        .from('payment_orders')
        .select('*')
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (ordersError) {
        console.error('Error fetching paginated orders:', ordersError);
        throw ordersError;
      }
      
      // Calculate total pages
      const calculatedTotalCount = totalCount || 0;
      const totalPages = Math.max(1, Math.ceil(calculatedTotalCount / pageSize));
      
      // Update state
      setData({
        totalCount: calculatedTotalCount,
        statusCounts,
        recentOrders: recentOrdersData || [],
        orders: ordersData || [],
        pagination: {
          page,
          pageSize,
          totalPages,
          totalCount: calculatedTotalCount
        }
      });
      
      setCurrentPage(page);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching payment orders:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setLoading(false);
      toast.error('Failed to load payment data');
    }
  };

  useEffect(() => {
    safeExecute(
      () => {
        console.log('Initializing OrderStatsViewer and fetching data...');
        fetchPaymentOrders(1);
        return;
      },
      () => {
        setLoading(false);
        setError("Could not initialize the orders viewer in this environment.");
        return;
      }
    );
    
    // Set up an interval to refresh data every 30 seconds
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing payment data...');
      fetchPaymentOrders(currentPage);
    }, 30000); // 30 seconds
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [adminUser]);

  const handlePageChange = (page: number) => {
    console.log('Changing page to:', page);
    fetchPaymentOrders(page);
  };
  
  const handleRefresh = () => {
    console.log('Manually refreshing data...');
    toast.info('Refreshing payment data...');
    fetchPaymentOrders(currentPage);
  };

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Orders</h2>
          <p className="mb-2">There was a problem connecting to the database:</p>
          <p className="font-mono text-sm bg-gray-100 p-2 rounded mb-4">{error}</p>
          <button
            onClick={() => fetchPaymentOrders(1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (loading && data.totalCount === 0) {
    return (
      <Card className="p-6">
        <div className="flex flex-col justify-center items-center min-h-[300px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-500">Loading payment data...</p>
        </div>
      </Card>
    );
  }

  if (data.totalCount === 0 && !loading) {
    return (
      <Card className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-4">No Payment Records Found</h2>
          <p className="mb-3">There are currently no payment orders in the database.</p>
          <p className="mb-6 text-sm text-gray-600">
            Try running <code className="bg-gray-100 px-2 py-1 rounded">createTestOrder()</code> in the browser console to add test data.
          </p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 21h5v-5" />
          </svg>
          Refresh Now
        </button>
      </div>
      
      <OrdersSummary 
        totalCount={data.totalCount} 
        statusCounts={data.statusCounts} 
        recentOrders={data.recentOrders} 
      />
      <OrdersView 
        orders={data.orders} 
        pagination={data.pagination} 
        onPageChange={handlePageChange} 
      />
    </div>
  );
};

export default OrderStatsViewer;
